import type { FC, ReactNode } from "react"
import { createContext, useContext } from "react"

interface AlgoliaIndexes {
  collections: string
  products: string
  experiences: string
  questions: string
  capsulePages: string
}

export type AlgoliaIndexName = keyof AlgoliaIndexes

interface BasinFormIds {
  contact: string
  press: string
  careers: string
}

export interface PublicConfigValues {
  production: boolean
  frontendBase: string
  backendBase: string
  backendProxy: boolean
  contentfulAccessToken: string
  contentfulSpace: string
  contentfulEnvironment: string
  shopifyDomain: string
  shopifyToken: string
  algoliaAppId: string
  algoliaApiKey: string
  algoliaIndexes: AlgoliaIndexes
  googleAnalyticsMeasurementId: string
  googleRecaptchaSiteKey: string
  basinFormIds: BasinFormIds
  freeShippingCutoff: number
  cheapItemShippingCutoff: number
  cheapItemShippingRequirement: number
  bopisEnabled: boolean
  stubActivityTypeId?: string
  slimeBarActivityTypeId?: string
  stubCollectionId?: string
  stubProductIds: string[]
  stubArchetypeProductIds?: string[]
  stubStaffPickProductIds?: string[]
  bestsellersCollectionHandle: string
  giftPickerCollectionHandle: string
  googleMapsApiKey: string
  growthBookClientKey: string
  webhookRedirect?: boolean
  webhookRedirectUrl?: string
  myShowPageRedirect?: boolean
  gtmId: string
  disableGTM: boolean
  microsoftClarityProjectId?: string
  gtmLoadTimeout?: number
  sellingPlanId?: string
  monthlyPassMembershipSellingPlanId?: string
  disableEcomm: boolean
  ecommAllowedPaths: string[]
  sentryTracesSampleRate: number
  sentryReplaysSampleRate: number
  sentryDsn: string
  sentryEnvironment: string
  htmlCacheMaxAgeS: number
  htmlCacheSMaxAgeS: number
}

export interface PrivateConfigValues {
  password: string | null
  backendBaseInternal: string | null
  /**
   * Shared secret proving a backend request really came from this server, for
   * headers the backend must not accept from arbitrary public callers (see
   * `X-Client-Ip` in routes/api.referral-create.ts). Must match the API's
   * INTERNAL_REQUEST_SECRET; null when unset, in which case the backend
   * ignores those headers.
   */
  internalRequestSecret: string | null
}

const ConfigContext = createContext<undefined | PublicConfigValues>(undefined)

interface Props {
  values: PublicConfigValues
  children: ReactNode
}

export const ConfigProvider: FC<Props> = ({ values, children }) => {
  return (
    <ConfigContext.Provider value={values}>{children}</ConfigContext.Provider>
  )
}

export const useConfigContext = () => {
  const config = useContext(ConfigContext)

  if (!config) {
    throw new Error("ConfigProvider is missing.")
  }

  return config
}

export const getPublicConfig = (
  request: Request | null
): PublicConfigValues => {
  const preview = request
    ? new URL(request.url).searchParams.has("preview")
    : false

  return {
    production: process.env.NODE_ENV === "production",
    frontendBase: frontendUrl(),
    backendBase: process.env.BACKEND_URL || publicBackendUrl(),
    backendProxy: process.env.BACKEND_PROXY == "true",
    contentfulAccessToken: preview
      ? requireEnvironmentVariable("CONTENTFUL_PREVIEW_ACCESS_TOKEN")
      : requireEnvironmentVariable("CONTENTFUL_ACCESS_TOKEN"),
    contentfulSpace: requireEnvironmentVariable("CONTENTFUL_SPACE"),
    contentfulEnvironment: requireEnvironmentVariable("CONTENTFUL_ENVIRONMENT"),
    shopifyDomain: requireEnvironmentVariable("SHOPIFY_DOMAIN"),
    shopifyToken: requireEnvironmentVariable("HEADLESS_SHOPIFY_TOKEN"),
    algoliaAppId: requireEnvironmentVariable("ALGOLIA_APP_ID"),
    algoliaApiKey: requireEnvironmentVariable("ALGOLIA_API_KEY"),
    algoliaIndexes: {
      collections: "Shopify_Collection",
      products: "Shopify_Product",
      experiences: "ActivityType",
      questions: "Question",
      capsulePages: "CapsulePage"
    },
    googleAnalyticsMeasurementId: requireEnvironmentVariable(
      "GOOGLE_ANALYTICS_MEASUREMENT_ID"
    ),
    googleRecaptchaSiteKey: requireEnvironmentVariable(
      "GOOGLE_RECAPTCHA_SITE_KEY"
    ),
    googleMapsApiKey: requireEnvironmentVariable("GOOGLE_MAPS_API_KEY"),
    // Defaults are the production Basin forms; set these to point local,
    // staging, and preview deploys at the staging Basin account instead.
    basinFormIds: {
      contact: process.env.BASIN_CONTACT_FORM_ID || "119f4e130593",
      press: process.env.BASIN_PRESS_FORM_ID || "52fadc484883",
      careers: process.env.BASIN_CAREERS_FORM_ID || "9f8c6bd7ea30"
    },
    freeShippingCutoff: 75,
    cheapItemShippingCutoff: 0, // original 14, holiday 0 - disabled
    cheapItemShippingRequirement: 0, // original 30, holiday 0 - disabled
    bopisEnabled: process.env.BOPIS_ENABLED == "true",
    stubActivityTypeId: process.env.STUB_ACTIVITY_TYPE_ID,
    slimeBarActivityTypeId: process.env.SLIME_BAR_ACTIVITY_TYPE_ID,
    stubCollectionId: process.env.STUB_COLLECTION_ID,
    stubProductIds: process.env.STUB_PRODUCT_IDS
      ? process.env.STUB_PRODUCT_IDS.split(",")
      : [],
    stubArchetypeProductIds: process.env.STUB_ARCHETYPE_PRODUCT_IDS
      ? process.env.STUB_ARCHETYPE_PRODUCT_IDS.split(",")
      : [],
    stubStaffPickProductIds: process.env.STUB_STAFF_PICK_PRODUCT_IDS
      ? process.env.STUB_STAFF_PICK_PRODUCT_IDS.split(",")
      : [],
    bestsellersCollectionHandle: "bestsellers",
    giftPickerCollectionHandle: "gift-picker",
    growthBookClientKey: requireEnvironmentVariable("GROWTH_BOOK_CLIENT_KEY"),
    webhookRedirect: process.env.WEBHOOK_REDIRECT === "true",
    webhookRedirectUrl: webhookRedirectUrl(),
    myShowPageRedirect: process.env.MY_SHOW_PAGE_REDIRECT === "true",
    gtmId: requireEnvironmentVariable("GTM_ID"),
    disableGTM: process.env.DISABLE_GTM === "true",
    microsoftClarityProjectId: process.env.MICROSOFT_CLARITY_PROJECT_ID,
    gtmLoadTimeout: parseInt(process.env.GTM_LOAD_TIMEOUT || "8000"),
    sellingPlanId: process.env.TOP_BUNK_MEMBERSHIP_SELLING_PLAN_ID,
    monthlyPassMembershipSellingPlanId:
      process.env.MONTHLY_PASS_MEMBERSHIP_SELLING_PLAN_ID,
    disableEcomm: process.env.DISABLE_ECOMM === "true",
    ecommAllowedPaths: parseCommaSeparatedEnvVar(
      process.env.ECOMM_ALLOWED_PATHS
    ),
    sentryTracesSampleRate: parseFloat(
      process.env.SENTRY_TRACES_SAMPLE_RATE || "0.2"
    ),
    sentryReplaysSampleRate: parseFloat(
      process.env.SENTRY_REPLAYS_SAMPLE_RATE || "0.2"
    ),
    sentryDsn: requireEnvironmentVariable("SENTRY_DSN"),
    sentryEnvironment: requireEnvironmentVariable("SENTRY_ENVIRONMENT"),
    htmlCacheMaxAgeS: parseInt(process.env.HTML_CACHE_MAX_AGE_S || "5"),
    htmlCacheSMaxAgeS: parseInt(process.env.HTML_CACHE_S_MAXAGE_S || "5")
  }
}

export const getPrivateConfig = (_request: Request): PrivateConfigValues => {
  return {
    backendBaseInternal: internalBackendUrl(),
    password: process.env.PASSWORD ?? null,
    internalRequestSecret: process.env.INTERNAL_REQUEST_SECRET ?? null
  }
}

export const requireEnvironmentVariable = (name: string) => {
  const value = process.env[name]
  if (!value) {
    throw new Error(`Missing environment variable: ${name}`)
  }

  return value
}

const parseCommaSeparatedEnvVar = (value: string | undefined) => {
  if (!value) {
    return []
  }

  const parsed = value
    .split(",")
    .map(part => part.trim())
    .filter(Boolean)

  return parsed
}

const frontendUrl = () => {
  // Production
  if (process.env.FRONTEND_EXTERNAL_URL) {
    return process.env.FRONTEND_EXTERNAL_URL
  }

  // Staging - current service URL
  if (process.env.RENDER_EXTERNAL_URL) {
    return process.env.RENDER_EXTERNAL_URL
  }

  // Development
  return requireEnvironmentVariable("FRONTEND_URL")
}

const publicBackendUrl = () => {
  // Production
  if (process.env.BACKEND_EXTERNAL_URL) {
    return process.env.BACKEND_EXTERNAL_URL
  }

  // Staging - customer service URL
  if (process.env.BACKEND_HOST) {
    return `https://${process.env.BACKEND_HOST}.onrender.com`
  }

  // Development
  return requireEnvironmentVariable("BACKEND_URL")
}

const internalBackendUrl = () => {
  // Staging and production
  if (process.env.BACKEND_HOST && process.env.BACKEND_PORT) {
    return `http://${process.env.BACKEND_HOST}:${process.env.BACKEND_PORT}`
  }

  // Development
  return requireEnvironmentVariable("BACKEND_URL")
}

const webhookRedirectUrl = () => {
  return `${publicBackendUrl()}/shopify/webhooks`
}
